import { memo, useEffect, useState } from "react";
import { validationSchema } from "./Validation.schema";
import dayjs from "dayjs";

// Utility: treat 'Removal' and 'Removal, Door to Door' as equivalent for validation
export function isRemovalOrDoorToDoor(typeName:any) {
  if (!typeName) return false;
  const normalized = typeName.trim().toLowerCase();
  return (
    normalized === "removal" ||
    normalized === "removal,door to door" ||
    normalized === "removal, door to door"
  );
}

interface ValidationOptions {
  // Field ids that should skip the "cannot be in the past" check.
  // Default: applies everywhere (original behaviour). Only pages that
  // explicitly pass this option opt out for the named fields — every
  // other page/component calling Validation(states) without a second
  // argument keeps the noPastTime rule exactly as before.
  skipPastTimeCheck?: string[];
}

const Validation = (
  states: any,
  options: ValidationOptions = {},
  ...props: any
): any => {
  const [error, setError]: any = useState(null);

  useEffect(() => {
    if (error === null) {
      const statesArray: any = Object.keys(states);
      const er: any = {};
      for (let i = 0; i < statesArray.length; i++) {
        er[statesArray[i]] = null;
      }
      setError(er);
    }
  }, [states]);
  const checkValidator = (keyArr = null) => {
    const statesArray = keyArr ? [keyArr] : Object.keys(states);
    let result = {};
    statesArray.map((key, i) => {
      const schema = validationSchema[key];
      if (schema?.required) {
        if (
          schema.properties?.empty &&
          (states[key] === "" || states[key] === null)
        ) {
         
          result = {
            ...result,
            [key]: schema.properties?.empty,
          };
        }
        // Additional validation: Check for space as the first character
        else if (
          schema.properties?.noLeadingSpace &&
          states[key]?.startsWith(" ")
        ) {
          result = {
            ...result,
            [key]: schema.properties?.noLeadingSpace,
          };
        } else if (
          schema.properties?.atLeastOneAlpha &&
          !/[a-zA-Z]/.test(states[key])
        ) {
          result = {
            ...result,
            [key]: schema.properties?.atLeastOneAlpha,
          };
        } else if (
          schema.properties?.alphanumeric &&
          !/^([a-zA-Z0-9]+)$/.test(states[key])
        ) {
          result = {
            ...result,
            [key]: schema.properties?.alphanumeric,
          };
        } else if (
          schema.properties?.postCode &&
          !/^([a-zA-Z0-9 ]+)$/.test(states[key])
        ) {
          result = {
            ...result,
            [key]: schema.properties?.postCode,
          };
        } else if (
          schema.properties?.maxLength &&
          states[key] &&
          states[key].length > schema.properties?.maxLength?.limit
        ) {
          result = {
            ...result,
            [key]: schema?.properties?.maxLength?.msg,
          };
        }else if (
          schema?.properties?.uppercase &&
          !/[A-Z]/.test(states[key])
        ) {
          result = {
            ...result,
            [key]: schema?.properties?.uppercase,
          };
        } else if (
          schema.properties?.specialCharacter &&
          !/[!@#$%^&*(),.?":{}|<>]/.test(states[key])
        ) {
          result = {
            ...result,
            [key]: schema?.properties?.specialCharacter,
          };
        } 
        // Check for no past date/time
        else if (schema.properties?.noPastDate) {
          const now = dayjs();
          const selectedDateTime = dayjs(states[key]);  // Get selected date and time
          if (selectedDateTime.isBefore(now, 'minute')) {
           
            result = {
              ...result,
              [key]: schema.properties?.noPastDate,
            };
          }else{
           
            result = {
              ...result,
              [key]: null,
          }
        }

          // Validation: Ensure startTime is not greater than endTime
          if (key === "startTime" && states["endTime"]) {
            const startTime = dayjs(states["startTime"]);
            const endTime = dayjs(states["endTime"]);
            const now = dayjs();
          // const selectedDateTime = dayjs(states[key]);
            if (startTime.isSame(endTime)) {
              result = {
                ...result,
                startTime: schema.properties?.sameAsEnd,
              };
            }
            else if (startTime.isBefore(now, 'minute')) {
              result = {
                ...result,
                startTime: schema.properties?.noPastDate,
              };
            }
            else if (startTime.isAfter(endTime)) {
              result = {
                ...result,
                startTime: schema.properties?.afterEnd,
              };
            }else{
              result = {
                ...result,
                startTime: null,
              }
            }
          }

        // Check if endTime is after startTime
        else if (key === "endTime" && schema.properties?.afterStartTime) {
          const startTime = dayjs(states["startTime"]);
          const endTime = dayjs(states["endTime"]);
          const now = dayjs();
          if (endTime.isSame(startTime)) {
            result = {
              ...result,
              endTime: schema.properties?.sameAsStart,
            };
          } else if (endTime.isBefore(now, 'minute')) {
            result = {
              ...result,
              endTime: schema.properties?.noPastDate,
            };
          }
           else if (dayjs(states[key]).isBefore(dayjs(states.startTime))) {
            result = {
              ...result,
              endTime: schema.properties?.afterStartTime,
            };
          }else{
            result = {
              ...result,
              [key]: null,
            }
          }
        }
        
       }
       // New validation for past time only
       else if (schema.properties?.noPastTime) {
        let fieldTime:any = dayjs(); // Get current date and time
        const selectedDateTime = states[key];  // Get selected date and time
        const selectedTime = dayjs(selectedDateTime, "HH:mm:ss");  // Convert to Day.js object
        if(schema.dependentField && states[schema.dependentField]){
          fieldTime = dayjs(states[schema.dependentField]).hour(selectedDateTime.split(":")[0]).minute(selectedDateTime.split(":")[1]);
          // fieldTime += 'T'+states[key]+'.000Z'
          // fieldTime = dayjs(fieldTime)
         
        }
        // const now = dayjs();  // Get current date and time
 
      
        // Extract selected time in 'HH:mm:ss' format and convert to Day.js object
        const currentTime = dayjs();  // Current time as a Day.js object

        // Applies to ALL pages by default. A page only skips this rule
        // for a given field if it explicitly passes
        // { skipPastTimeCheck: ["start_time", ...] } as the 2nd arg to
        // Validation(states, options). No options passed => unchanged
        // behaviour (past time still blocked), exactly as before.
        const skipPastTimeCheck =
          Array.isArray(options?.skipPastTimeCheck) &&
          options.skipPastTimeCheck.includes(key);
      
        // Log the times for debugging
      
        // Compare selected time with current time
        if (!skipPastTimeCheck && fieldTime.diff(currentTime) <= 0) {
          result = {
            ...result,
            [key]: schema.properties?.noPastTime,
          };
        } else if (key === "start_time" && states["end_time"]) {
          // Validate start_time if it exists
          const startTime = dayjs(states["start_time"], "HH:mm:ss");  // Convert to Day.js object
          const endTime = dayjs(states["end_time"], "HH:mm:ss");      // Convert to Day.js object
      
          // Log for debugging

      
          // Compare startTime with endTime
          if (startTime.isSame(endTime)) {
            result = {
              ...result,
              [key]: schema.properties?.sameAsEnd,
            };
          } else if (startTime.isAfter(endTime)) {
            result = {
              ...result,
              [key]: schema.properties?.afterEnd,
            };
          } else {
            result = {
              ...result,
              [key]: null,  // No error
            };
          }
        } 
        // Validate end_time if it exists
        else if (key === "end_time" && states["start_time"]) {
          const startTime = dayjs(states["start_time"], "HH:mm:ss");  // Convert to Day.js object
          const endTime = dayjs(states["end_time"], "HH:mm:ss");      // Convert to Day.js object
    
      
          // Compare endTime with startTime
          if (endTime.isSame(startTime)) {
            result = {
              ...result,
              [key]: schema.properties?.sameAsStart,
            };
          } else if (endTime.isBefore(startTime)) {
            result = {
              ...result,
              [key]: schema.properties?.afterStartTime,
            };
          } else {
            result = {
              ...result,
              [key]: null,  // No error
            };
          }
        } else {
          result = {
            ...result,
            [key]: null,  // No error
          };
        }
      }
        else if (
          schema.properties?.minLength &&
          states[key] &&
          states[key].length < schema.properties?.minLength.limit
        ) {
          result = {
            ...result,
            [key]: schema.properties?.minLength.msg,
          };
        } else if (schema?.properties?.email) {
          const trimmedEmail = states[key]?.trim() ?? "";
          const position = states[key]?.lastIndexOf("@");
          const indexOfDot = states[key]?.lastIndexOf(".");
          if (
            trimmedEmail !== "" &&
            position > 0 &&
            indexOfDot > position &&
            states[key].length - position > 4
          ) {
            result = {
              ...result,
              [key]: null,
            };
          } else {
            result = {
              ...result,
              [key]: schema.properties?.email,
            };
          }
        } else if (schema.properties?.number && /[a-zA-Z]/.test(states[key])) {
          result = {
            ...result,
            [key]: schema.properties?.number.msg,
          };
        } else if (
          schema?.properties?.maxNumber &&
          parseInt(states[key]) >= schema?.properties?.maxNumber?.limit
        ) {
          result = {
            ...result,
            [key]: schema.properties?.maxNumber?.msg,
          };
        } else if (key === "proposedPassword") {
          const err: any = {};

          if (states[key] && states[key].length < 8) {
            err.length = true;
          }
          if (states[key] && !/[a-z]/.test(states[key])) {
            err.lowercase = true;
          }
          if (states[key] && !/[A-Z]/.test(states[key])) {
            err.uppercase = true;
          }
          if (states[key] && !/[0-9]/.test(states[key])) {
            err.number = true;
          }
          if (states[key] && !/[!@#\$%\^\&*\)\(+=._-]/.test(states[key])) {
            err.special = true;
          }
          result = {
            ...result,
            [key]: Object.keys(err).length ? err : null,
          };
        } else if (key === "confirmPassword") {
          if (states[key] !== (states["proposedPassword"] || states["password"])) {
            result = {
              ...result,
              [key]: schema.properties.match,
            };
          } else {
            result = {
              ...result,
              [key]: null,
            };
          }
        } else {
          result = {
            ...result,
            [key]: null,
          };
        }
        return null;
      } else {
        const value = states[key];
        const hasOptionalValue =
          value !== "" && value !== null && value !== undefined;

        if (hasOptionalValue) {
          if(schema?.properties?.dependent){
            if(states[schema?.properties?.dependent?.key] && states[schema?.properties?.dependent?.key] === schema?.properties?.dependent?.value) {
              if(!states[key]) {
                result = {
                  ...result,
                  [key]: schema?.properties?.dependent?.msg,
                };
              } else {
                result = {
                  ...result,
                  [key]: null,
                };
              }
            } else {
              result = {
                ...result,
                [key]: null,
              };
            }
          }else if (
            schema?.properties?.websiteLink &&
            states[key] !== "" &&
            !/^((https?:\/\/)?(www\.)?[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,})(\/[^\s]*)?$/.test(
              states[key]
            )
          ) {
            result = {
              ...result,
              [key]: schema.properties?.websiteLink,
            };
          } else if (
            schema?.properties?.number &&
            /[a-zA-Z]/.test(states[key])
          ) {
            result = {
              ...result,
              [key]: schema.properties?.number.msg,
            };
          } else if (
            schema?.properties?.maxLength &&
            states[key] &&
            states[key].length > schema.properties?.maxLength?.limit
          ) {
            result = {
              ...result,
              [key]: schema.properties?.maxLength?.msg,
            };
          } else if (
            schema?.properties?.minLength &&
            states[key] &&
            states[key].length < schema.properties?.minLength.limit
          ) {
            result = {
              ...result,
              [key]: schema.properties?.minLength.msg,
            };
          } else if (
            schema?.properties?.maxNumber &&
            parseInt(states[key]) >= schema?.properties?.maxNumber?.limit
          ) {
            result = {
              ...result,
              [key]: schema.properties?.maxNumber?.msg,
            };
          } else if (
            schema?.properties?.email ||
            schema?.properties?.notificationEmail ||
            schema?.properties?.emailContact ||
            schema?.properties?.staff_email
          ) {
            const trimmedEmail = states[key]?.trim() ?? "";
            const position = states[key]?.lastIndexOf("@");
            const indexOfDot = states[key]?.lastIndexOf(".");
            if (
              trimmedEmail !== "" &&
              position > 0 &&
              indexOfDot > position &&
              states[key].length - position > 4
            ) {
              result = {
                ...result,
                [key]: null,
              };
            } else {
              result = {
                ...result,
                [key]: schema.properties?.email,
              };
            }
          }
        } else {
          result = {
            ...result,
            [key]: null,
          };
        }
      }
    });

    setError({ ...error, ...result });
  };

  const resetAllValidator = (key: any) => {
    setError(null);
  };

  const resetValidator = (key: any) => {
    setError({
      ...error,
      [key]: false,
    });
  };

  const isError = () => {
    if (error === null) return true;
    const statesArray = Object.keys(states);
    for (let i = 0; i < statesArray.length; i++) {
      const schema = validationSchema[statesArray[i]];
      if (error[statesArray[i]] && schema?.required) {
        return true;
      }
    }
    for (let i = 0; i < statesArray.length; i++) {
      const schema = validationSchema[statesArray[i]];
      if (!states[statesArray[i]] && schema?.required) {
        return true;
      }
    }
    return false;
  };

  const isErrorOptional = () => {
    if (error === null) return true;
    const statesArray = Object.keys(states);
    for (let i = 0; i < statesArray.length; i++) {
      const schema = validationSchema[statesArray[i]];
      if (error[statesArray[i]])
        if (error[statesArray[i]] && !schema?.required) {
          return true;
        }
    }
    return false;
  };

  return [
    error,
    checkValidator,
    resetValidator,
    resetAllValidator,
    isError,
    isErrorOptional,
  ];
};

export default Validation;